import { AfterViewInit, Directive, ElementRef, inject, input } from '@angular/core';

// PrimeNG's ConfirmDialog wires aria-modal / aria-labelledby and the initial focus to its DEFAULT
// header + accept/reject buttons. A headless template replaces all of that, so every confirm popup
// loses those a11y guarantees (no accessible name, no modal hint, focus left on the trigger).
// Applied once to the shared headless card, this restores them for every confirm dialog at once.
@Directive({
  selector: '[cesConfirmDialogA11y]',
  standalone: true,
})
export class ConfirmDialogA11yDirective implements AfterViewInit {
  private readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  /** Element id naming the dialog (its title) → aria-labelledby. */
  readonly labelledBy = input<string>('');
  /** Element id describing the dialog (its message) → aria-describedby. */
  readonly describedBy = input<string>('');

  ngAfterViewInit(): void {
    const dialog = this.host.nativeElement.closest<HTMLElement>('[role="alertdialog"],[role="dialog"]');
    if (!dialog) return;

    dialog.setAttribute('aria-modal', 'true');
    if (this.labelledBy()) dialog.setAttribute('aria-labelledby', this.labelledBy());
    if (this.describedBy()) dialog.setAttribute('aria-describedby', this.describedBy());
    if (!dialog.hasAttribute('tabindex')) dialog.setAttribute('tabindex', '-1');

    // PrimeNG's focusOnShow honours defaultFocus="none" (required by the headless template) by
    // leaving focus on <body>. Re-assert focus on the container once the 150ms entry animation has
    // settled, but only if focus is still outside — focusTrap then keeps it in, and we never steal
    // focus from a control the user already tabbed to. This lets screen readers announce the title
    // + message, then Tab moves to the buttons.
    const focusDialog = () => {
      if (dialog.contains(document.activeElement)) return;
      dialog.focus();
      // If focusing the tabindex=-1 container did not take (a PrimeNG/browser quirk), fall back to
      // the first real control so focus still enters the modal; focusTrap then keeps it inside.
      if (document.activeElement !== dialog) {
        dialog.querySelector<HTMLElement>('button')?.focus();
      }
    };
    setTimeout(focusDialog);
    setTimeout(focusDialog, 200);
  }
}
